import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/lib/supabase';
import { useAuth } from '@/hooks/useAuth';
import { useLocationStore } from '@/store/location';
import { useEffect, useMemo, useRef } from 'react';
import { useTheme } from '@/components/theme-provider';
import { SYSTEM_FONTS, type SystemFont } from '@/hooks/useFeatureFlags';

export type ThemePreset = 'blaze' | 'lightspeed';

export interface AppearanceSettings {
  accentColor?: string;
  theme?: 'light' | 'dark' | 'system';
  font?: SystemFont;
  preset?: ThemePreset;
}

interface OrganizationSettings {
  appearance?: AppearanceSettings;
  ai_upsell_enabled?: boolean;
  openai_api_key?: string;
}

interface LocationSettings {
  appearance?: AppearanceSettings;
  icon?: string;
  icon_url?: string;
  hide_default_payment_types?: boolean;
}

interface UserSettings {
  appearance?: {
    theme?: 'light' | 'dark' | 'system';
  };
}

const DEFAULT_APPEARANCE: AppearanceSettings = {
  accentColor: '#f97316',
  theme: 'system',
  font: 'inter',
};

export function useAppearanceSettings() {
  const queryClient = useQueryClient();
  const { organization, profile, user } = useAuth();
  const { currentLocation } = useLocationStore();
  const { setAccentColor } = useTheme();

  // Use refs to track what we've applied (persists across renders but resets on unmount)
  const lastAppliedAccentColorRef = useRef<string | null>(null);
  const lastAppliedFontRef = useRef<string | null>(null);
  // Track if we've done initial application from DB
  const hasAppliedFromDB = useRef(false);

  // Fetch organization settings
  const { data: orgSettings, isLoading: orgLoading } = useQuery({
    queryKey: ['org-settings', organization?.id],
    queryFn: async () => {
      if (!organization?.id) return null;

      const { data, error } = await supabase
        .from('organizations')
        .select('settings')
        .eq('id', organization.id)
        .single();

      if (error) throw error;
      return (data?.settings as OrganizationSettings) || {};
    },
    enabled: !!organization?.id,
  });

  // Fetch current location settings
  const { data: locationSettings, isLoading: locationLoading } = useQuery({
    queryKey: ['location-settings', currentLocation?.id],
    queryFn: async () => {
      if (!currentLocation?.id) return null;

      const { data, error } = await supabase
        .from('locations')
        .select('settings')
        .eq('id', currentLocation.id)
        .single();

      if (error) throw error;
      return (data?.settings as LocationSettings) || {};
    },
    enabled: !!currentLocation?.id,
  });

  // Fetch user settings (for theme preference)
  const { data: userSettings, isLoading: userLoading } = useQuery({
    queryKey: ['user-settings', user?.id],
    queryFn: async () => {
      if (!user?.id) return null;

      const { data, error } = await supabase
        .from('profiles')
        .select('settings')
        .eq('id', user.id)
        .single();

      if (error) throw error;
      return (data?.settings as UserSettings) || {};
    },
    enabled: !!user?.id,
  });

  // Merge appearance settings: defaults -> org -> location
  // Theme is handled separately by ThemeProvider using localStorage
  // Accent color and font come from org/location (admin-controlled)
  const effectiveAppearance: AppearanceSettings = useMemo(() => ({
    ...DEFAULT_APPEARANCE,
    ...(orgSettings?.appearance || {}),
    ...(locationSettings?.appearance || {}),
  }), [orgSettings?.appearance, locationSettings?.appearance]);

  // Check if queries are actually loading OR if they haven't been enabled yet
  // When organization?.id is undefined, the query is disabled but isLoading is false
  const isLoading = orgLoading || locationLoading || userLoading;
  const queriesEnabled = !!organization?.id; // Only org settings matter for accent color
  const isReady = !isLoading && queriesEnabled && orgSettings !== undefined;

  // Apply accent color and font from org/location settings whenever they change
  // This ensures DB values override localStorage defaults after page refresh
  useEffect(() => {
    // Don't apply until queries are ready with real data
    // This prevents applying defaults before DB data is fetched
    if (!isReady) {
      return;
    }

    const dbAccentColor = effectiveAppearance.accentColor;
    const dbFont = effectiveAppearance.font;

    // On initial load from DB, always apply the DB values to ensure they take precedence
    // over any localStorage values that ThemeProvider may have initialized with
    if (!hasAppliedFromDB.current) {
      if (dbAccentColor) {
        setAccentColor(dbAccentColor);
        lastAppliedAccentColorRef.current = dbAccentColor;
      }
      if (dbFont) {
        const fontConfig = SYSTEM_FONTS.find(f => f.value === dbFont);
        if (fontConfig) {
          document.documentElement.style.setProperty('--system-font', fontConfig.fontFamily);
        }
        lastAppliedFontRef.current = dbFont;
      }
      hasAppliedFromDB.current = true;
      return;
    }

    // After initial application, only apply if DB value differs from what we last applied
    // This prevents unnecessary re-applications during normal use
    if (dbAccentColor && dbAccentColor !== lastAppliedAccentColorRef.current) {
      setAccentColor(dbAccentColor);
      lastAppliedAccentColorRef.current = dbAccentColor;
    }

    if (dbFont && dbFont !== lastAppliedFontRef.current) {
      const fontConfig = SYSTEM_FONTS.find(f => f.value === dbFont);
      if (fontConfig) {
        document.documentElement.style.setProperty('--system-font', fontConfig.fontFamily);
      }
      lastAppliedFontRef.current = dbFont;
    }
  }, [isReady, effectiveAppearance.accentColor, effectiveAppearance.font, setAccentColor]);

  // Update user's personal theme preference (saves to DB for sync, but localStorage is source of truth)
  const updateUserTheme = useMutation({
    mutationFn: async (theme: 'light' | 'dark' | 'system') => {
      if (!user?.id) throw new Error('No user');

      const newSettings: UserSettings = {
        ...(userSettings || {}),
        appearance: {
          ...(userSettings?.appearance || {}),
          theme,
        },
      };

      const { error } = await supabase
        .from('profiles')
        .update({ settings: newSettings })
        .eq('id', user.id);

      if (error) throw error;
      
      // Note: The actual theme change is handled by ThemeProvider via localStorage
      // This DB save is for syncing preferences across devices
      return theme;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['user-settings', user?.id] });
    },
  });

  // Update organization appearance (admin only)
  const updateOrgAppearance = useMutation({
    mutationFn: async (appearance: AppearanceSettings) => {
      if (!organization?.id) throw new Error('No organization');

      // Fetch current settings fresh from DB to avoid race conditions
      const { data: currentData, error: fetchError } = await supabase
        .from('organizations')
        .select('settings')
        .eq('id', organization.id)
        .single();

      if (fetchError) throw fetchError;

      const currentSettings = (currentData?.settings as OrganizationSettings) || {};
      const newSettings: OrganizationSettings = {
        ...currentSettings,
        appearance,
      };

      const { error } = await supabase
        .from('organizations')
        .update({ settings: newSettings })
        .eq('id', organization.id);

      if (error) throw error;

      // Update the tracking ref to prevent re-application
      if (appearance.accentColor) {
        lastAppliedAccentColorRef.current = appearance.accentColor;
      }
      if (appearance.font) {
        lastAppliedFontRef.current = appearance.font;
      }

      return appearance;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['org-settings', organization?.id] });
    },
  });

  // Update location appearance - accepts locationId parameter
  const updateLocationAppearance = useMutation({
    mutationFn: async ({ appearance, locationId }: { appearance: AppearanceSettings; locationId: string }) => {
      if (!locationId) throw new Error('No location selected');

      // First fetch current settings for the location
      const { data: currentData, error: fetchError } = await supabase
        .from('locations')
        .select('settings')
        .eq('id', locationId)
        .single();

      if (fetchError) throw fetchError;

      const currentSettings = (currentData?.settings as LocationSettings) || {};
      const newSettings: LocationSettings = {
        ...currentSettings,
        appearance,
      };

      const { error } = await supabase
        .from('locations')
        .update({ settings: newSettings })
        .eq('id', locationId);

      if (error) throw error;
      return appearance;
    },
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ['location-settings', variables.locationId] });
    },
  });

  // Clear location appearance override - accepts locationId parameter
  const clearLocationAppearance = useMutation({
    mutationFn: async (locationId: string) => {
      if (!locationId) throw new Error('No location selected');

      // First fetch current settings for the location
      const { data: currentData, error: fetchError } = await supabase
        .from('locations')
        .select('settings')
        .eq('id', locationId)
        .single();

      if (fetchError) throw fetchError;

      const currentSettings = (currentData?.settings as LocationSettings) || {};
      const newSettings: LocationSettings = {
        ...currentSettings,
        appearance: undefined,
      };

      const { error } = await supabase
        .from('locations')
        .update({ settings: newSettings })
        .eq('id', locationId);

      if (error) throw error;
    },
    onSuccess: (_, locationId) => {
      queryClient.invalidateQueries({ queryKey: ['location-settings', locationId] });
    },
  });

  // Check if user can edit org-wide settings
  const canEditOrgSettings = profile?.role === 'superadmin' || profile?.role === 'primary_admin';

  // Check if current location has override
  const hasLocationOverride = !!locationSettings?.appearance;

  // Check if user has personal theme override
  const hasUserThemeOverride = !!userSettings?.appearance?.theme;

  return {
    // Settings
    orgAppearance: orgSettings?.appearance || DEFAULT_APPEARANCE,
    locationAppearance: locationSettings?.appearance,
    userTheme: userSettings?.appearance?.theme,
    effectiveAppearance,
    hasLocationOverride,
    hasUserThemeOverride,

    // Loading state - true until we have actual data from DB
    // This prevents consumers from using defaults before real data arrives
    isLoading: !isReady,

    // Mutations
    updateOrgAppearance,
    updateLocationAppearance,
    clearLocationAppearance,
    updateUserTheme,

    // Permissions
    canEditOrgSettings,
  };
}
